import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Edit, ShieldCheck, ShieldOff, KeyRound } from 'lucide-react';

const ROLE_LABELS = { global_admin: 'Global Admin', admin: 'Admin', staff: 'Staff' };
const ROLE_STYLES = { global_admin: 'bg-purple-50 text-purple-700 border-purple-200', admin: 'bg-indigo-50 text-indigo-700 border-indigo-200', staff: 'bg-zinc-100 text-zinc-600 border-zinc-200' };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isGlobalAdmin = currentUser?.role === 'global_admin';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [resetPw, setResetPw] = useState({ userId: null, password: '' });

  const load = () => { api.get('/users').then(r => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', form);
      setShowModal(false); setForm({ name: '', email: '', password: '', role: 'staff' }); toast.success('User created'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updates = {};
      if (form.name) updates.name = form.name;
      if (form.email) updates.email = form.email;
      if (form.role) updates.role = form.role;
      await api.patch(`/users/${editUser.id}`, updates);
      setEditUser(null); setShowModal(false); toast.success('User updated'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleToggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleResetPassword = async () => {
    if (!resetPw.password) return;
    try {
      await api.post(`/users/${resetPw.userId}/reset-password`, { new_password: resetPw.password });
      setResetPw({ userId: null, password: '' }); toast.success('Password updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="p-6" data-testid="users-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-zinc-500">Manage user accounts</p>
        <Button size="sm" onClick={() => { setShowModal(true); setEditUser(null); setForm({ name: '', email: '', password: '', role: 'staff' }); }}
          data-testid="invite-user-btn" className="rounded-sm text-xs gap-1.5" style={{ background: 'var(--brand-primary, #1C3557)' }}>
          <Plus size={13} /> Invite user
        </Button>
      </div>

      {loading ? <div className="text-sm text-zinc-400 py-8">Loading...</div> : (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Name</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Email</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Role</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Status</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="px-4 py-2.5 font-semibold text-zinc-800">{u.name}</TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-500">{u.email}</TableCell>
                  <TableCell className="px-4 py-2.5">
                    <Badge variant="outline" className={`rounded-sm text-[10px] font-semibold ${ROLE_STYLES[u.role] || 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}>{ROLE_LABELS[u.role] || u.role}</Badge>
                  </TableCell>
                  <TableCell className="px-4 py-2.5">
                    <Badge variant="outline" className={`rounded-sm text-[10px] font-semibold ${u.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>{u.is_active ? 'Active' : 'Inactive'}</Badge>
                  </TableCell>
                  <TableCell className="px-4 py-2.5">
                    <div className="flex gap-1">
                      <button onClick={() => { setEditUser(u); setForm({ name: u.name, email: u.email, role: u.role }); setShowModal(true); }} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><Edit size={14} /></button>
                      <button onClick={() => handleToggleActive(u)} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100">{u.is_active ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}</button>
                      <button onClick={() => setResetPw({ userId: u.id, password: '' })} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><KeyRound size={14} /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={(v) => { setShowModal(v); if (!v) setEditUser(null); }}>
        <DialogContent className="sm:max-w-[380px] rounded-sm">
          <DialogHeader><DialogTitle className="text-base font-bold">{editUser ? 'Edit user' : 'Invite user'}</DialogTitle></DialogHeader>
          <form onSubmit={editUser ? handleUpdate : handleCreate} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-sm h-9" required />
            </div>
            {!editUser && <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Temporary password</Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="rounded-sm h-9" required />
            </div>}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Role</Label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="flex h-9 w-full rounded-sm border border-input bg-transparent px-3 py-1 text-sm">
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                {isGlobalAdmin && <option value="global_admin">Global Admin</option>}
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 rounded-sm" style={{ background: 'var(--brand-primary, #1C3557)' }}>{editUser ? 'Update' : 'Create'}</Button>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1 rounded-sm">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetPw.userId} onOpenChange={() => setResetPw({ userId: null, password: '' })}>
        <DialogContent className="sm:max-w-[340px] rounded-sm">
          <DialogHeader><DialogTitle className="text-base font-bold">Reset password</DialogTitle></DialogHeader>
          <Input type="password" value={resetPw.password} onChange={e => setResetPw({ ...resetPw, password: e.target.value })} placeholder="New password" className="rounded-sm h-9" />
          <div className="flex gap-2">
            <Button onClick={handleResetPassword} className="flex-1 rounded-sm" style={{ background: 'var(--brand-primary, #1C3557)' }}>Reset</Button>
            <Button variant="outline" onClick={() => setResetPw({ userId: null, password: '' })} className="flex-1 rounded-sm">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
